<template>
  <div>
    <div class="header">
      <div class="container-fluid">

        <b-row class="align-items-center mt-3 mb-2">
          <b-col>
            <span class="text-muted" v-if="solicitudesFiltradas.length !== solicitudes.length">
              {{ solicitudesFiltradas.length }} / {{ solicitudes.length }} {{ $t('resultado_solicitudes') }}
            </span>
            <span class="text-muted" v-else>{{ solicitudes.length }} {{ $t('resultado_solicitudes') }}</span>
          </b-col>
          <b-col class="text-right">
            <b-button variant="success" size="sm" class="mr-2" @click="abrirNuevaSolicitud">
              <i class="fas fa-plus mr-1"></i>
              {{ $t('nueva_solicitud') }}
            </b-button>
            <b-button variant="primary" size="sm" @click="cargarSolicitudes" :disabled="cargando">
              <b-spinner small v-if="cargando" class="mr-1" />
              {{ $t('refrescar') }}
            </b-button>
          </b-col>
        </b-row>

        <b-alert variant="danger" :show="!!error" dismissible @dismissed="error = ''">{{ error }}</b-alert>

        <div v-if="cargando" class="text-center text-muted mt-5">
          <b-spinner variant="primary" />
        </div>

        <div v-else-if="solicitudes.length" class="card shadow">
          <div class="tabla-scroll-fixed" ref="tablaSolicitudes">
            <table class="table table-hover align-items-center mb-0">
              <thead class="thead-sticky thead-light">
                <tr>
                  <!-- Ancho fijo y acotado: el numero de solicitud son pocos digitos. -->
                  <th class="th-ordenable" style="width:90px;min-width:90px;" @click="ordenarPor('id')">
                    <input class="th-search-input" v-model="filterCol.id" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('n_solicitud') }} <i :class="iconoOrden('id')"></i></span>
                  </th>
                  <th class="th-ordenable" @click="ordenarPor('fechaRegistro')">
                    <span class="th-label">{{ $t('fecha') }} <i :class="iconoOrden('fechaRegistro')"></i></span>
                  </th>
                  <th class="th-ordenable" style="width:100%;min-width:520px;" @click="ordenarPor('mensaje')">
                    <input class="th-search-input" v-model="filterCol.mensaje" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('mensaje') }} <i :class="iconoOrden('mensaje')"></i></span>
                  </th>
                  <th class="th-ordenable text-center" @click="ordenarPor('estado')">
                    <input class="th-search-input" v-model="filterCol.estado" @click.stop :placeholder="$t('buscar')" />
                    <span class="th-label">{{ $t('estado') }} <i :class="iconoOrden('estado')"></i></span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in solicitudesPaginadas" :key="s.id">
                  <td><strong>#{{ s.id }}</strong></td>
                  <td>{{ formatFecha(s.fechaRegistro) }}</td>
                  <!-- El mensaje se recorta a un ancho fijo; el texto completo
                       queda en el tooltip para no romper el alto de la fila. -->
                  <td>
                    <span class="celda-mensaje" v-b-tooltip.hover.top="s.mensaje || ''">
                      {{ s.mensaje || '-' }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span :class="badgeClass(s.estado)">{{ labelEstado(s.estado) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Barra de paginación y scroll fija en el pie -->
        <div v-if="solicitudes.length" class="pagination-floating-bar">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 text-secondary" style="font-size:.85rem;">{{ $t('items_por_pagina') || 'Items / pág.' }}</label>
            <select v-model.number="pageSize" @change="currentPage = 1" class="form-control form-control-sm" style="width:70px;">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="999999">Todas</option>
            </select>
          </div>
          <div class="d-flex align-items-center" style="gap:12px;">
            <button class="btn btn-outline-secondary btn-sm" :disabled="currentPage === 1" @click="currentPage--">
              <i class="fas fa-arrow-left mr-1"></i> {{ $t('anterior') || 'Anterior' }}
            </button>
            <span style="font-weight:600;color:#6c757d;font-size:.9rem;">
              {{ $t('pagina') || 'Página' }} {{ currentPage }} {{ $t('de') || 'de' }} {{ totalPages }}
              <span class="text-muted ml-2" style="font-size:.8rem;">({{ solicitudesFiltradas.length }} {{ $t('resultados') || 'resultados' }})</span>
            </span>
            <button class="btn btn-outline-primary btn-sm" :disabled="currentPage >= totalPages" @click="currentPage++">
              {{ $t('siguiente') || 'Siguiente' }} <i class="fas fa-arrow-right ml-1"></i>
            </button>
          </div>
          <div class="d-flex align-items-center" style="gap:6px;">
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('left')" title="Scroll izquierda"><i class="fas fa-chevron-left"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('right')" title="Scroll derecha"><i class="fas fa-chevron-right"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('top')" title="Ir al inicio"><i class="fas fa-chevron-up"></i></button>
            <button class="btn btn-sm btn-scroll-nav" @click="scrollTabla('bottom')" title="Ir al final"><i class="fas fa-chevron-down"></i></button>
          </div>
        </div>

        <div v-else-if="!cargando" class="text-center text-muted mt-5">
          <i class="fas fa-inbox fa-3x mb-3"></i>
          <p>{{ $t('sin_solicitudes') }}</p>
        </div>

      </div>
    </div>

    <!-- Modal nueva solicitud -->
    <b-modal v-model="modalNuevaVisible" :title="$t('nueva_solicitud')" size="md" no-enforce-focus @hidden="resetNuevaSolicitud">
      <!-- Nombre, correo, teléfono y empresa viajan en el envío pero no se
           muestran: salen del usuario en sesión y no son editables aquí. Se
           cargan en segundo plano y el botón de enviar espera a que estén. -->
      <b-form-group>
        <template v-slot:label>
          {{ $t('mensaje') }} <span class="text-danger">*</span>
        </template>
        <b-form-textarea
          v-model="nuevaSolicitud.mensaje"
          rows="6"
          max-rows="10"
          :placeholder="$t('mensaje_solicitud_placeholder')"
        />
      </b-form-group>

      <b-alert variant="danger" :show="!!errorNuevaSolicitud" dismissible @dismissed="errorNuevaSolicitud = ''">
        {{ errorNuevaSolicitud }}
      </b-alert>

      <template #modal-footer>
        <b-button variant="secondary" @click="modalNuevaVisible = false">{{ $t('cancelar') }}</b-button>
        <b-button
          variant="primary"
          :disabled="guardandoNuevaSolicitud || cargandoDatosUsuario || !nuevaSolicitud.mensaje.trim()"
          @click="guardarNuevaSolicitud"
        >
          <b-spinner small v-if="guardandoNuevaSolicitud || cargandoDatosUsuario" class="mr-1" />
          {{ $t('enviar') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import peticionProductoService from '@/api/peticionProductoService';
import userService from '@/api/userService';
import empresaService from '@/api/empresaService';
import utilsService from '@/api/utilsService';

export default {
  name: 'SolicitudesClienteView',

  data() {
    return {
      solicitudes: [],
      cargando: false,
      error: '',

      // Tabla: filtros por columna, orden y paginación
      filterCol: { id: '', mensaje: '', estado: '' },
      ordenColumna: 'fechaRegistro',
      ordenDireccion: 'desc',
      currentPage: 1,
      pageSize: 20,

      // Nueva solicitud
      modalNuevaVisible: false,
      nuevaSolicitud: { mensaje: '' },
      datosUsuario: { nombre: '', email: '', fono: '', empresa: '' },
      cargandoDatosUsuario: false,
      guardandoNuevaSolicitud: false,
      errorNuevaSolicitud: '',
    };
  },

  watch: {
    filterCol: { deep: true, handler() { this.currentPage = 1; } },
  },

  mounted() {
    this.cargarSolicitudes();
    this._scrollHandler = this._onWindowScrollSolicitudes.bind(this);
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this._scrollHandler);
  },

  computed: {
    solicitudesFiltradas() {
      const fc = this.filterCol;
      let lista = this.solicitudes;
      if (fc.id)      lista = lista.filter(s => String(s.id || '').toLowerCase().includes(fc.id.toLowerCase()));
      if (fc.mensaje) lista = lista.filter(s => (s.mensaje || '').toLowerCase().includes(fc.mensaje.toLowerCase()));
      if (fc.estado)  lista = lista.filter(s => this.labelEstado(s.estado).toLowerCase().includes(fc.estado.toLowerCase()));
      return lista;
    },

    solicitudesOrdenadas() {
      if (!this.ordenColumna) return this.solicitudesFiltradas;
      const sorted = [...this.solicitudesFiltradas];
      sorted.sort((a, b) => {
        let vA = a[this.ordenColumna];
        let vB = b[this.ordenColumna];
        if (vA == null) vA = '';
        if (vB == null) vB = '';
        if (typeof vA === 'number' && typeof vB === 'number') return this.ordenDireccion === 'asc' ? vA - vB : vB - vA;
        vA = String(vA).toLowerCase();
        vB = String(vB).toLowerCase();
        if (vA < vB) return this.ordenDireccion === 'asc' ? -1 : 1;
        if (vA > vB) return this.ordenDireccion === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    },

    totalPages() {
      if (this.pageSize >= 999999) return 1;
      return Math.max(1, Math.ceil(this.solicitudesOrdenadas.length / this.pageSize));
    },

    solicitudesPaginadas() {
      if (this.pageSize >= 999999) return this.solicitudesOrdenadas;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.solicitudesOrdenadas.slice(start, start + this.pageSize);
    },
  },

  methods: {
    async cargarSolicitudes() {
      this.cargando = true;
      this.error = '';
      try {
        this.solicitudes = await peticionProductoService.getMisSolicitudes();
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || 'Error al cargar las solicitudes.';
      } finally {
        this.cargando = false;
      }
    },

    ordenarPor(columna) {
      if (this.ordenColumna === columna) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = columna;
        this.ordenDireccion = 'asc';
      }
      this.currentPage = 1;
    },

    iconoOrden(columna) {
      if (this.ordenColumna !== columna) return 'fas fa-sort text-muted';
      return this.ordenDireccion === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },

    scrollTabla(dir) {
      const el = this.$refs.tablaSolicitudes;
      const px = 300;
      if (dir === 'left')   el && el.scrollBy({ left: -px, behavior: 'smooth' });
      if (dir === 'right')  el && el.scrollBy({ left:  px, behavior: 'smooth' });
      if (dir === 'top')    window.scrollTo({ top: 0, behavior: 'smooth' });
      if (dir === 'bottom') window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    },

    _onWindowScrollSolicitudes() {
      const cont = this.$refs.tablaSolicitudes;
      if (!cont) return;
      const thead = cont.querySelector('thead.thead-sticky');
      if (!thead) return;
      const top = cont.getBoundingClientRect().top;
      thead.style.transform = top < 0 ? `translateY(${-top}px)` : '';
    },

    async abrirNuevaSolicitud() {
      this.resetNuevaSolicitud();
      this.modalNuevaVisible = true;
      await this.cargarDatosUsuario();
    },

    // El token solo trae nombre, correo y el id de la empresa; el teléfono y el
    // nombre de la empresa hay que pedirlos. Para cliente y comprador el backend
    // devuelve siempre su propio usuario, sin importar el id que se envíe.
    async cargarDatosUsuario() {
      this.cargandoDatosUsuario = true;
      try {
        const info = utilsService.getUserInfo() || {};
        this.datosUsuario.nombre = info.username || '';

        const usuario = await userService.get(info.id);
        if (usuario) {
          this.datosUsuario.nombre = [usuario.nombre, usuario.apellidoP].filter(Boolean).join(' ') || this.datosUsuario.nombre;
          this.datosUsuario.email = usuario.correo || '';
          this.datosUsuario.fono = usuario.telefono || '';

          const empresas = await empresaService.getEmpresasByUser();
          const propia = (empresas || []).find(e => e.id === usuario.idEmpresa) || (empresas || [])[0];
          this.datosUsuario.empresa = propia ? (propia.nombre || '') : '';
        }
      } catch (e) {
        this.errorNuevaSolicitud = this.$t('error_cargar_datos_usuario');
      } finally {
        this.cargandoDatosUsuario = false;
      }
    },

    async guardarNuevaSolicitud() {
      const mensaje = (this.nuevaSolicitud.mensaje || '').trim();
      if (!mensaje) return;

      this.guardandoNuevaSolicitud = true;
      this.errorNuevaSolicitud = '';
      try {
        // Mismo contrato que el formulario público del ecommerce, pero con el
        // tipo 'I' (Solicitud desde App MM, dbo.Diccionarios Tipo = 'SOL-ECOM'),
        // que distingue lo ingresado desde el portal interno. Deja la solicitud
        // en estado pendiente y dispara los correos de aviso.
        await peticionProductoService.create({
          nombre:  this.datosUsuario.nombre,
          email:   this.datosUsuario.email,
          fono:    this.datosUsuario.fono || null,
          empresa: this.datosUsuario.empresa,
          mensaje,
          idTipo:  'I',
        });

        this.modalNuevaVisible = false;
        this.$toast ? this.$toast.success(this.$t('solicitud_enviada')) : null;
        await this.cargarSolicitudes();
      } catch (e) {
        this.errorNuevaSolicitud =
          (e && e.response && e.response.data && e.response.data.message) || this.$t('error_enviar_solicitud');
      } finally {
        this.guardandoNuevaSolicitud = false;
      }
    },

    resetNuevaSolicitud() {
      this.nuevaSolicitud = { mensaje: '' };
      this.errorNuevaSolicitud = '';
      this.guardandoNuevaSolicitud = false;
    },

    labelEstado(estado) {
      const map = { P: this.$t('estado_por_asignar'), A: this.$t('estado_en_proceso'), C: this.$t('estado_cerrado') };
      return map[estado] || estado;
    },

    badgeClass(estado) {
      const map = {
        P: 'badge badge-pill badge-warning',
        A: 'badge badge-pill badge-info',
        C: 'badge badge-pill badge-success',
      };
      return map[estado] || 'badge badge-pill badge-light';
    },

    formatFecha(fecha) {
      if (!fecha) return '-';
      return new Date(fecha).toLocaleDateString('es-CL');
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 10px;
  overflow: hidden;
}

/* ===== Tabla con cabecera inmóvil ===== */
.tabla-scroll-fixed {
  overflow-x: auto;
  position: relative;
  font-size: 0.8rem;
}

.thead-sticky {
  position: relative;
  z-index: 2;
  background: #f8f9fa;
}

.th-ordenable {
  cursor: pointer;
  user-select: none;
  min-width: 90px;
  vertical-align: top;
  white-space: nowrap;
}
.th-ordenable:hover {
  background-color: #e9ecef !important;
}

.th-search-input {
  width: 100%;
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #ced4da;
  border-radius: 3px;
  margin-bottom: 3px;
  display: block;
  font-weight: normal;
}

.th-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.tabla-scroll-fixed .table th,
.tabla-scroll-fixed .table td {
  font-size: 12px;
}

/* Mensaje en una sola línea: se corta con puntos suspensivos y el texto
   completo se consulta en el tooltip. */
.celda-mensaje {
  display: inline-block;
  max-width: 700px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  cursor: help;
}

/* ===== Botones de scroll ===== */
.btn-scroll-nav {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ced4da;
  background: #f8f9fa;
  color: #495057;
  border-radius: 6px;
}
.btn-scroll-nav:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

/* ===== Barra flotante de paginación ===== */
.pagination-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 24px;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}
</style>
